import React, { useEffect, useRef, useContext, useCallback, useMemo } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";

const ZoomableCanvas = (props) => {
  const [contextData] = useContext(VariantContextData);

  const minScale = props.minScale || 0.25;
  const pinchZoomRatio = props.pinchZoomRatio || 2;
  const mouseZoomRatio = props.mouseZoomRatio || 3;
  const panningRatio = props.panningRatio || 0.75;

  const currentScale = useRef(props.initialScale || 1);
  const transformX = useRef(props.initialX || 0);
  const transformY = useRef(props.initialY || 0);
  const mousePosition = useRef({ startPositionX: 0, startPositionY: 0 });

  const zoomableDom = useRef(null);
  const animationFrameId = useRef(null);
  const lastControlsUpdateTimestamp = useRef(0);

  const updateZoomControls = useCallback(() => {
    document.querySelectorAll(".cf-zoom-control-percentage").forEach((zoomable_percentage) => {
      if (zoomable_percentage) {
        zoomable_percentage.innerHTML = `${Math.round(
          currentScale.current * 100
        )}%`;
      }
    });
  }, []);

  const updateCanvas = useCallback(() => {
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    animationFrameId.current = requestAnimationFrame(() => {
      if (zoomableDom.current) {
        zoomableDom.current.style.transform = `matrix(${currentScale.current}, 0, 0, ${currentScale.current}, ${transformX.current}, ${transformY.current})`;

        const now = Date.now();
        if (now - lastControlsUpdateTimestamp.current >= 100) {
          updateZoomControls();
          lastControlsUpdateTimestamp.current = now;
        }
      }
    });
  }, [updateZoomControls]);

  const parsePanningDeltasWithinLimit = useCallback(
    (calculatedX, calculatedY) => {
      if (!zoomableDom.current) return;

      let scrollHeight = zoomableDom.current.scrollHeight;
      let minX = 2000 * currentScale.current * -1;
      let maxX =
        currentScale.current > 0.7
          ? 500 * currentScale.current
          : 3500 * currentScale.current;
      let minY = Math.abs(scrollHeight) * currentScale.current * -1;
      let maxY = Math.abs(scrollHeight);

      if (calculatedX >= minX && calculatedX <= maxX) {
        transformX.current = calculatedX;
      } else if (calculatedX < minX) {
        transformX.current = minX;
      } else if (calculatedX > maxX) {
        transformX.current = maxX;
      }

      if (calculatedY >= minY && calculatedY <= maxY) {
        transformY.current = calculatedY;
      } else if (calculatedY < minY) {
        transformY.current = minY;
      } else if (calculatedY > maxY) {
        transformY.current = maxY;
      }
    },
    []
  );

  const handlePinchZoom = useCallback(
    (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        const calculated_ratio =
          Math.abs(event.deltaY) > 10
            ? Math.sign(event.deltaY) * mouseZoomRatio
            : event.deltaY * pinchZoomRatio;

        const newScale =
          currentScale.current * Math.exp(-calculated_ratio / 100);
        const ratio = 1 - newScale / currentScale.current;
        const { clientX, clientY } = event;

        currentScale.current =
          newScale >= 0.25 && newScale <= 2
            ? newScale
            : newScale < 0.25
            ? 0.25
            : newScale > 2
            ? 2
            : 1;
        const calculatedX =
          transformX.current + (clientX - transformX.current) * ratio;
        const calculatedY =
          transformY.current + (clientY - transformY.current) * ratio;

        parsePanningDeltasWithinLimit(calculatedX, calculatedY);
        updateCanvas();
      }
    },
    [
      mouseZoomRatio,
      pinchZoomRatio,
      parsePanningDeltasWithinLimit,
      updateCanvas,
    ]
  );

  const handlePanning = useCallback(
    (event) => {
      if (!event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();

        const direction = -1;
        const calculatedY =
          transformY.current + event.deltaY * panningRatio * direction;
        const calculatedX =
          transformX.current + event.deltaX * panningRatio * direction;

        parsePanningDeltasWithinLimit(calculatedX, calculatedY);
        updateCanvas();
      }
    },
    [panningRatio, parsePanningDeltasWithinLimit, updateCanvas]
  );

  const handleWheelPanZoom = useCallback(
    (event) => {
      handlePinchZoom(event);
      handlePanning(event);
    },
    [handlePinchZoom, handlePanning]
  );

  const handleMouseStart = useCallback((event) => {
    mousePosition.current.startPositionX = event.pageX - transformX.current;
    mousePosition.current.startPositionY = event.pageY - transformY.current;
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      if (event.which === 2) {
        event.stopPropagation();
        event.preventDefault();

        transformX.current = event.pageX - mousePosition.current.startPositionX;
        transformY.current = event.pageY - mousePosition.current.startPositionY;
        updateCanvas();
      }
    },
    [updateCanvas]
  );

  const handleStopImmediatePropagation = useCallback((event) => {
    event.stopImmediatePropagation();
  }, []);

  const resetCanvas = useCallback(() => {
    currentScale.current =
      contextData.device === "mobile" ? 1 : props.initialScale || 1;
    transformX.current =
      contextData.device === "mobile" ? 0 : props.initialX || 0;
    transformY.current = props.initialY || 0;
    mousePosition.current = { startPositionX: 0, startPositionY: 0 };

    updateCanvas();
  }, [
    props.initialScale,
    props.initialX,
    props.initialY,
    contextData.device,
    updateCanvas,
  ]);

  const zoomIn = useCallback(() => {
    const newScale = currentScale.current + 0.25;
    currentScale.current = newScale > 2 ? 2 : newScale;
    updateCanvas();
  }, [updateCanvas]);

  const zoomOut = useCallback(() => {
    const newScale = currentScale.current - 0.25;
    currentScale.current = newScale < minScale ? minScale : newScale;
    updateCanvas();
  }, [minScale, updateCanvas]);

  useEffect(() => {
    resetCanvas();
  }, [contextData.zoomable_canvas_locked, resetCanvas]);

  useEffect(() => {
    resetCanvas();
  }, [contextData.device, resetCanvas]);

  useEffect(() => {
    const variantEditElement = document.querySelector(".variant-edit");
    const optionsPanelElement = document.querySelector("#options-panel");
    const onboardPopupElement = document.querySelector("#onboard-popup");
    const zoomablePercentageElement = document.querySelector(
      "#cf-zoom-control-percentage"
    );

    const onZoomPercentageMouseOver = () => {
      if (zoomablePercentageElement)
        zoomablePercentageElement.innerHTML = "Reset";
    };
    const onZoomPercentageMouseLeave = () => {
      if (zoomablePercentageElement)
        zoomablePercentageElement.innerHTML = `${Math.round(
          currentScale.current * 100
        )}%`;
    };

    if (contextData.zoomable_canvas_locked !== true) {
      if (zoomablePercentageElement) {
        zoomablePercentageElement.addEventListener(
          "mouseover",
          onZoomPercentageMouseOver
        );
        zoomablePercentageElement.addEventListener(
          "mouseleave",
          onZoomPercentageMouseLeave
        );
      }
      if (variantEditElement) {
        variantEditElement.addEventListener("wheel", handleWheelPanZoom, {
          passive: false,
        });
        variantEditElement.addEventListener("mousedown", handleMouseStart, {
          passive: false,
        });
        variantEditElement.addEventListener("mousemove", handleMouseMove, {
          passive: false,
        });
      }
      if (optionsPanelElement) {
        optionsPanelElement.addEventListener(
          "wheel",
          handleStopImmediatePropagation
        );
      }
      if (onboardPopupElement) {
        onboardPopupElement.addEventListener(
          "wheel",
          handleStopImmediatePropagation
        );
      }
    }

    document.addEventListener("zoomIn", zoomIn);
    document.addEventListener("zoomOut", zoomOut);
    document.addEventListener("resetCanvas", resetCanvas);

    updateCanvas();

    return function cleanup() {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
      document.removeEventListener("zoomIn", zoomIn);
      document.removeEventListener("zoomOut", zoomOut);
      document.removeEventListener("resetCanvas", resetCanvas);

      if (zoomablePercentageElement) {
        zoomablePercentageElement.removeEventListener(
          "mouseover",
          onZoomPercentageMouseOver
        );
        zoomablePercentageElement.removeEventListener(
          "mouseleave",
          onZoomPercentageMouseLeave
        );
      }
      if (variantEditElement) {
        variantEditElement.removeEventListener("wheel", handleWheelPanZoom);
        variantEditElement.removeEventListener("mousedown", handleMouseStart);
        variantEditElement.removeEventListener("mousemove", handleMouseMove);
      }
      if (optionsPanelElement) {
        optionsPanelElement.removeEventListener(
          "wheel",
          handleStopImmediatePropagation
        );
      }
      if (onboardPopupElement) {
        onboardPopupElement.removeEventListener(
          "wheel",
          handleStopImmediatePropagation
        );
      }
    };
  }, [
    contextData.zoomable_canvas_locked,
    handleWheelPanZoom,
    handleMouseStart,
    handleMouseMove,
    handleStopImmediatePropagation,
    zoomIn,
    zoomOut,
    resetCanvas,
    updateCanvas,
  ]);

  const canvasStyle = useMemo(() => ({
    transformOrigin: contextData.zoomable_canvas_locked ? "top" : "0 0",
  }), [contextData.zoomable_canvas_locked]);

  return (
    <React.Fragment>
      <div ref={zoomableDom} id="cf-zoomable-canvas" style={canvasStyle}>
        {props.children}
      </div>
      <style
        dangerouslySetInnerHTML={{
          __html: style.stylesheet(contextData, canvasStyle.transformOrigin),
        }}
      />
    </React.Fragment>
  );
};

const style = {
  stylesheet: (contextData, transformOriginValue) => {
    const desktopStyles = `body {
        overflow-x: hidden;
      }

      #cf-zoomable-canvas {
        transform-origin: ${transformOriginValue};
        will-change: transform;
        backface-visibility: hidden;
        margin-left: auto;
        margin-right: auto;
        width: 100%;
      }
      .cf-zoom-controls {
        position: fixed;
        bottom: 5px;
        right: 300px;
        height: 36px;
        width: auto;
        z-index: 1;
        border: 1px solid #ddd;
        display: inline-flex;
        border-radius: 7px;
        padding: 0px;
        box-sizing: border-box;
        font-size: 17px;
        background-color: #fff;
        color: #000;
        margin-top: 15px;
        margin-bottom: 10px;
      }

      .cf-zoom-controls .cf-zoom-control-zoom-out {
        padding-left: 15px;
      }

      .cf-zoom-controls .cf-zoom-control-zoom-in {
        padding-right: 15px;
      }

      .cf-zoom-controls .cf-zoom-control-zoom-in,
      .cf-zoom-controls #cf-zoom-control-percentage,
      .cf-zoom-controls .cf-zoom-control-zoom-out,
      .cf-zoom-controls .cf-zoom-control-reset {
        padding: 5px 12px;
        cursor: pointer;
        -webkit-touch-callout: none;
        /* iOS Safari */
        -webkit-user-select: none;
        /* Safari */
        -khtml-user-select: none;
        /* Konqueror HTML */
        -moz-user-select: none;
        /* Old versions of Firefox */
        -ms-user-select: none;
        /* Internet Explorer/Edge */
        user-select: none;
        /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
      }
      `;

    const mobileStyles = `
      #cf-zoomable-canvas .variant-edit-canvas {
        padding-left: initial !important;
        padding-right: initial !important;
      }
    `;

    if (contextData.device == "desktop") {
      return desktopStyles;
    } else {
      return desktopStyles + mobileStyles;
    }
  },
};

export default ZoomableCanvas;
